import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerFinancialLegalInvoiceRoute,
  leagueCustomerFinancialPaymentRoute,
  leagueCustomerFinancialPaymentVoidCreditRoute,
} from "network/api-routes";
import { useSnackbar } from "notistack";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import GestionTable from "./GestionTable";
import { financialAccountHref } from "./financial/financial-account-deep-link";
import { formatFinancialAmount } from "./financial/financial-helpers";
import {
  VOID_REASON_MIN_LENGTH,
  isVoidableCredit,
  moneyRequestErrorMessage,
  voidCreditErrorMessage,
} from "./financial/money-validation";
import { TRANSACTIONS_TAB_PARAM, gatewayName, maskedCard, paymentMethodLabel } from "./financial/transaction-details";

const formatDateTime = (value) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : null);

const Field = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <RevaText variant="caption" color="text.secondary">
      {label}
    </RevaText>
    <RevaText variant="body1" sx={{ wordBreak: "break-word" }}>
      {children || "—"}
    </RevaText>
  </Grid>
);

const Section = ({ title, children }) => (
  <Card variant="outlined">
    <CardContent>
      <RevaText variant="h6" sx={{ mb: 2 }}>
        {title}
      </RevaText>
      {children}
    </CardContent>
  </Card>
);

const AccountLink = ({ customerId, account }) => {
  if (!account) return "—";
  const href = financialAccountHref(customerId, account);
  const name = account.name || account.target_id;
  return href ? (
    <Link component={RouterLink} to={href}>
      {name}
    </Link>
  ) : (
    name
  );
};

const GestionPaymentDetailPage = () => {
  const { paymentId } = useParams();
  const { customerId } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidError, setVoidError] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [uploadingLegalInvoice, setUploadingLegalInvoice] = useState(false);

  // X-05 / D4: an "Agregar saldo" credit can be voided with a reason (min 10 characters).
  const handleVoidCredit = () => {
    if (voidReason.trim().length < VOID_REASON_MIN_LENGTH) return;
    setVoiding(true);
    setVoidError(null);
    performRequest(
      "POST",
      leagueCustomerFinancialPaymentVoidCreditRoute(customerId, payment.id),
      { reason: voidReason.trim() },
      true,
      user,
    )
      .then((response) => {
        setPayment((previous) => ({ ...previous, ...(response?.data || {}), status: response?.data?.status || "CANCELLED" }));
        setVoidOpen(false);
        setVoidReason("");
        enqueueSnackbar(intl.formatMessage({ id: "gestion.paymentDetail.voidCredit.done" }), { variant: "success" });
      })
      .catch((requestError) => setVoidError(voidCreditErrorMessage(requestError, intl)))
      .finally(() => setVoiding(false));
  };

  const loadPayment = useCallback(() => {
    if (!customerId || !paymentId || !user) return;
    setError(null);
    performRequest("GET", leagueCustomerFinancialPaymentRoute(customerId, paymentId), {}, true, user)
      .then((response) => setPayment(response.data))
      .catch((requestError) =>
        setError(requestError?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" })),
      );
  }, [customerId, intl, paymentId, user]);

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  const handleLegalInvoice = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploadingLegalInvoice(true);
    performRequest("POST", leagueCustomerFinancialLegalInvoiceRoute(customerId, payment.id), formData, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.paymentDetail.legalInvoiceUploaded" }), { variant: "success" });
        loadPayment();
      })
      .catch((requestError) => enqueueSnackbar(moneyRequestErrorMessage(requestError, intl), { variant: "error" }))
      .finally(() => setUploadingLegalInvoice(false));
  };

  if (!customerId) return null;

  const money = (value) => formatFinancialAmount(value, payment?.currency);
  const t = (id, values) => intl.formatMessage({ id: `gestion.paymentDetail.${id}` }, values);
  const isOnline = String(payment?.method || "").toLowerCase() === "online";
  const isSuccess = String(payment?.status || "").toLowerCase() === "success";

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Button
        component={RouterLink}
        to={`${GESTION_ROUTES.financials(customerId)}?tab=${TRANSACTIONS_TAB_PARAM}`}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        {t("back")}
      </Button>

      {error && <Alert severity="error">{error}</Alert>}
      {!error && !payment && (
        <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {payment && (
        <Stack spacing={3}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <RevaText variant="h4">{t(isOnline ? "titleOnline" : "title", { id: payment.id })}</RevaText>
            <Chip
              label={intl.formatMessage({
                id: `gestion.paymentDetail.status.${String(payment.status).toLowerCase()}`,
                defaultMessage: payment.status,
              })}
              color={String(payment.status).toLowerCase() === "success" ? "success" : "default"}
            />
            <Box sx={{ flex: 1 }} />
            {isVoidableCredit(payment) && (
              <Button
                data-testid="void-credit"
                variant="outlined"
                color="error"
                onClick={() => {
                  setVoidError(null);
                  setVoidOpen(true);
                }}
              >
                {t("voidCredit.action")}
              </Button>
            )}
            {payment.receipt_url && (
              <Button variant="outlined" href={payment.receipt_url} target="_blank" rel="noopener noreferrer">
                {t("receipt")}
              </Button>
            )}
            {payment.proof_url && (
              <Button
                variant="outlined"
                href={payment.proof_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="payment-proof"
              >
                {t("proof")}
              </Button>
            )}
            {isSuccess && payment.legal_invoice_url && (
              <Button
                variant="outlined"
                href={payment.legal_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage({ id: "gestion.financials.downloadLegalInvoice" })}
              </Button>
            )}
            {isSuccess && (
              <Button variant="outlined" component="label" disabled={uploadingLegalInvoice}>
                {intl.formatMessage({ id: "gestion.financials.uploadLegalInvoice" })}
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.pdf"
                  data-testid="legal-invoice-input"
                  onChange={handleLegalInvoice}
                />
              </Button>
            )}
          </Stack>

          <Section title={t("summary")}>
            <Grid container spacing={2}>
              <Field label={t("amount")}>{money(payment.amount)}</Field>
              <Field label={t("fee")}>{money(payment.fee)}</Field>
              <Field label={t("net")}>{money(payment.net_amount)}</Field>
              <Field label={t("method")}>{paymentMethodLabel(payment.method, intl)}</Field>
              <Field label={t("account")}>
                <AccountLink customerId={customerId} account={payment.account} />
              </Field>
              <Field label={t("description")}>{payment.description}</Field>
            </Grid>
          </Section>

          {isOnline && (
            <Section title={t("gateway")}>
              <Grid container spacing={2}>
                <Field label={t("provider")}>{gatewayName(payment.gateway?.provider)}</Field>
                <Field label={t("transactionId")}>{payment.gateway?.transaction_id}</Field>
                <Field label={t("authorizationCode")}>{payment.gateway?.authorization_code}</Field>
                <Field label={t("ticketNumber")}>{payment.gateway?.ticket_number}</Field>
                <Field label={t("card")}>{maskedCard(payment.gateway)}</Field>
              </Grid>
            </Section>
          )}

          {isOnline && (
            <Section title={t("payer")}>
              <Grid container spacing={2}>
                <Field label={t("payerName")}>{payment.payer?.name}</Field>
                <Field label={t("payerDocument")}>{payment.payer?.document}</Field>
                <Field label={t("payerEmail")}>{payment.payer?.email}</Field>
                <Field label={t("payerPhone")}>{payment.payer?.phone_number}</Field>
              </Grid>
            </Section>
          )}

          <Section title={t("timeline")}>
            <Grid container spacing={2}>
              <Field label={t(isOnline ? "createdAt" : "createdAtManual")}>
                {formatDateTime(payment.timeline?.created_at)}
              </Field>
              {isOnline && (
                <Field label={t("checkoutStartedAt")}>{formatDateTime(payment.timeline?.checkout_started_at)}</Field>
              )}
              <Field label={t("successAt")}>{formatDateTime(payment.timeline?.success_at)}</Field>
              {isOnline && <Field label={t("expiresAt")}>{formatDateTime(payment.timeline?.expires_at)}</Field>}
            </Grid>
          </Section>

          <Section title={t("invoices")}>
            <GestionTable
              headers={[
                { key: "description", title: t("description") },
                {
                  key: "account",
                  title: t("account"),
                  renderCell: (_value, invoice) => <AccountLink customerId={customerId} account={invoice.account} />,
                },
                { key: "amountLabel", title: t("amount"), align: "right" },
                { key: "statusLabel", title: t("invoiceStatus") },
              ]}
              rows={(payment.invoices || []).map((invoice) => ({
                ...invoice,
                description: invoice.description || `#${invoice.id}`,
                amountLabel: money(invoice.amount),
                statusLabel: intl.formatMessage({
                  id: `gestion.playerFinance.status.${invoice.status}`,
                  defaultMessage: invoice.status,
                }),
              }))}
              emptyMessage={t("noInvoices")}
            />
          </Section>
        </Stack>
      )}
      <Dialog open={voidOpen} onClose={() => !voiding && setVoidOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("voidCredit.title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <RevaText variant="body2" color="text.secondary">
              {t("voidCredit.body", { amount: money(payment?.amount) })}
            </RevaText>
            {voidError && <Alert severity="error">{voidError}</Alert>}
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              label={t("voidCredit.reason")}
              value={voidReason}
              onChange={(event) => {
                setVoidReason(event.target.value);
                setVoidError(null);
              }}
              helperText={t("voidCredit.reasonHelp", { count: voidReason.trim().length, min: VOID_REASON_MIN_LENGTH })}
              inputProps={{ maxLength: 255, "data-testid": "void-credit-reason" }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)} disabled={voiding}>
            {intl.formatMessage({ id: "Cancelar" })}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleVoidCredit}
            disabled={voiding || voidReason.trim().length < VOID_REASON_MIN_LENGTH}
          >
            {t("voidCredit.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionPaymentDetailPage;
